import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  Crown,
  Clock,
  Gift,
  MessageCircleOff,
  Sparkles,
  Train,
  Trophy,
  TrendingUp,
  AlertTriangle,
  MapPinOff,
  Flame,
  Moon,
  Rocket,
  Skull,
} from 'lucide-react';
import {
  api,
  type AllianceHub,
  type AllianceHubAboveWeight,
  type AllianceHubBestTrainStart,
  type AllianceHubBusterWeek,
  type AllianceHubChatBanEntry,
  type AllianceHubGlowUp,
  type AllianceHubHighscore,
  type AllianceHubIronThumbs,
  type AllianceHubLastMegaExpress,
  type AllianceHubPlayer,
  type AllianceHubSleepingBeauty,
  type AllianceHubStrengthEntry,
  type AllianceHubStrengthTop,
  type AllianceHubTrainLeader,
  type AllianceHubTrainTile,
  type AllianceHubVsSlackers,
  type AllianceHubWorstParker,
  type SelfReportMe,
  type SelfReportMeVsDay,
} from '@/lib/api';
import PlayerHubHeader from '@/components/PlayerHubHeader';
import { usePlayerAlliance } from '@/lib/usePlayerAlliance';
import VsWeekBox from '@/components/VsWeekBox';
import { QUALITY_LABEL, hourQuality } from '@/lib/serverTime';
import { estimateHint } from '@/lib/strengthEstimate';

function currentMondayIso(): string {
  const now = new Date();
  const day = now.getUTCDay();
  const offset = day === 0 ? 6 : day - 1;
  const monday = new Date(now.getTime() - offset * 24 * 60 * 60 * 1000);
  return monday.toISOString().slice(0, 10);
}

const EMPTY_VS_DAYS: SelfReportMeVsDay[] = Array.from(
  { length: 6 },
  (_, i) => ({
    dayIndex: i,
    scoreTenthsM: null,
    dayTwentiethTenthsM: null,
    dailyWin: false,
    rank: null,
    totalPlayers: 0,
    othersAvgTenthsM: null,
  }),
);

export function playerLabel(p: AllianceHubPlayer): string {
  return p.displayName?.trim() || p.name;
}

function pad(n: number) {
  return n < 10 ? `0${n}` : String(n);
}

// Snapshot kills are already stored in millions — match the Self-Report
// formatter: up to 3 decimals, trailing zeros trimmed to a single digit.
function formatKillsM(n: number): string {
  return n.toFixed(3).replace(/0+$/, '').replace(/\.$/, '.0');
}

function formatCountdown(ms: number): string {
  if (ms <= 0) return 'ended';
  const total = Math.floor(ms / 1000);
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const mins = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  if (days > 0) return `${days}d ${pad(hours)}:${pad(mins)}:${pad(secs)}`;
  return `${pad(hours)}:${pad(mins)}:${pad(secs)}`;
}

export default function PlayerHubAlliance() {
  const params = useParams<{ token: string }>();
  const token = params.token ?? '';
  const q = useQuery({
    queryKey: ['alliance-hub', token],
    queryFn: () => api.getAllianceHub(token),
    enabled: !!token,
  });
  // Share cache with PlayerHubMe (same queryKey + default range).
  const meQuery = useQuery({
    queryKey: ['self-report-me', token, '90d'],
    queryFn: () => api.getSelfReportMe(token, '90d'),
    enabled: !!token,
  });
  const alliance = usePlayerAlliance(token);
  const allianceLabel = alliance ? alliance.tag || alliance.name : '';

  if (q.isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100">
        <div className="mx-auto max-w-md px-4 py-10 text-slate-400">Loading…</div>
      </div>
    );
  }
  if (q.isError || !q.data) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100">
        <div className="mx-auto max-w-md px-4 py-10">
          <h1 className="mb-2 text-xl font-semibold">Link not valid</h1>
          <p className="text-sm text-slate-400">
            This link is invalid or has been revoked.
          </p>
        </div>
      </div>
    );
  }

  const data: AllianceHub = q.data;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <PlayerHubHeader
        token={token}
        playerLabel={allianceLabel}
        playerRank=""
        subtitle="Alliance corner"
      />
      <main className="mx-auto max-w-md space-y-6 px-4 py-6">
        <VsCurrentWeek me={meQuery.data ?? null} />
        {data.megaExpressesToday.map((tile, i) => (
          <TrainCountdownTile key={`mega-${i}`} tile={tile} />
        ))}
        <TrainLeadersSection
          leaders={data.trainLeaders}
          today={data.today}
          weekStart={data.weekStart}
          weekDescription={data.trainWeekDescription}
        />
        {data.trainCountdown ? (
          <TrainCountdownTile tile={data.trainCountdown} />
        ) : (
          <BestTrainStartTile best={data.bestTrainStart} />
        )}
        <ChatBansSection entries={data.chatBans.active} />
        <ChatBanHighscoreSection highscores={data.chatBans.highscores} />
        <StrengthTopSection top={data.strengthTop} />
      </main>
    </div>
  );
}

function VsCurrentWeek({ me }: { me: SelfReportMe | null }) {
  if (!me) return null;
  const currentMonday = currentMondayIso();
  const current = me.vs.weeks.find((w) => w.weekStart === currentMonday);
  if (!current) return null;
  return (
    <section className="space-y-2">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-indigo-300">
        Current VS week
      </div>
      <VsWeekBox
        weekLabel={current.weekStart}
        meta={`${current.opponentName} · ${current.opponentServer}`}
        notes={current.notes}
        kind={current.kind}
        outcome={current.outcome}
        policy={me.vs.policy}
        weekScoreM={current.weekScoreM}
        twentiethM={current.twentiethM}
        days={current.days ?? EMPTY_VS_DAYS}
        rank={current.rank}
        totalPlayers={current.totalPlayers}
        othersAvgM={current.othersAvgM}
      />
    </section>
  );
}

function TrainLeadersSection({
  leaders,
  today,
  weekStart,
  weekDescription,
}: {
  leaders: AllianceHubTrainLeader[];
  today: string;
  weekStart: string;
  weekDescription: string | null;
}) {
  const todaysLeader = leaders.find((l) => l.date === today);
  const week = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart + 'T00:00:00Z');
    const iso = new Date(d.getTime() + i * 86400_000)
      .toISOString()
      .slice(0, 10);
    return { iso, leader: leaders.find((l) => l.date === iso) ?? null };
  });
  const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <section className="space-y-2">
      <SectionTitle icon={<Crown size={14} />} label="Train leaders" />

      {weekDescription && weekDescription.trim() !== '' && (
        <div className="whitespace-pre-wrap rounded-lg border border-slate-800 bg-slate-900/40 px-3 py-2 text-[11px] italic text-slate-300">
          {weekDescription}
        </div>
      )}

      {todaysLeader && (
        <div className="rounded-lg border border-amber-700/50 bg-amber-950/40 px-4 py-3">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-amber-300">
            Today's honorable leader
          </div>
          <div className="mt-1 flex items-center gap-2">
            <Crown size={18} className="text-amber-300" />
            <div className="text-base font-semibold text-amber-100">
              {playerLabel(todaysLeader.player)}
            </div>
          </div>
          {todaysLeader.notes && (
            <div className="mt-1 text-[11px] italic text-amber-200/80">
              "{todaysLeader.notes}"
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-7 gap-1">
        {week.map((d, i) => {
          const isToday = d.iso === today;
          const notes = d.leader?.notes?.trim() ?? '';
          return (
            <div
              key={d.iso}
              className={
                'flex min-w-0 flex-col items-center rounded-md border px-1 py-1.5 text-center text-[10px] ' +
                (isToday
                  ? 'border-amber-600/70 bg-amber-950/50 text-amber-100'
                  : d.leader
                    ? 'border-slate-700 bg-slate-900/40 text-slate-200'
                    : 'border-slate-800 bg-slate-900/20 text-slate-500')
              }
              title={
                d.leader
                  ? notes
                    ? `${playerLabel(d.leader.player)} — ${notes}`
                    : playerLabel(d.leader.player)
                  : 'No leader'
              }
            >
              <div className="font-semibold uppercase tracking-wider">
                {DAY_LABELS[i]}
              </div>
              <div className="mt-0.5 line-clamp-2 w-full min-w-0 leading-tight [overflow-wrap:anywhere]">
                {d.leader ? playerLabel(d.leader.player) : '—'}
              </div>
              {notes && (
                <div
                  className={
                    'mt-0.5 line-clamp-2 text-[9px] italic leading-tight ' +
                    (isToday ? 'text-amber-200/80' : 'text-slate-500')
                  }
                >
                  {notes}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function nightWindowLabel(): string {
  const hours = Array.from({ length: 24 }, (_, h) => h).filter(
    (h) => hourQuality(h) === 'bad',
  );
  if (hours.length === 0) return '';
  const pad = (n: number) => (n < 10 ? `0${n}` : String(n));
  const start = hours[0]!;
  const endExclusive = hours[hours.length - 1]! + 1;
  return `${pad(start)}–${pad(endExclusive)}`;
}

export function WorstParkerSection({
  pick,
}: {
  pick: AllianceHubWorstParker | null;
}) {
  if (!pick) return null;
  return (
    <section className="space-y-2">
      <SectionTitle icon={<MapPinOff size={14} />} label="Worst parker" />
      <div className="rounded-lg border border-rose-900/50 bg-rose-950/30 px-4 py-3">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-rose-300">
          Bad-teleport award of the week
        </div>
        <div className="mt-1 flex items-baseline justify-between gap-2">
          <span className="text-base font-semibold text-rose-100">
            {playerLabel(pick.player)}
          </span>
        </div>
        {pick.quip && (
          <div className="mt-1 text-[11px] italic text-rose-200/90">
            …{pick.quip}
          </div>
        )}
        {pick.note && pick.note.trim() !== '' && (
          <div className="mt-1 text-[11px] text-slate-300">
            {pick.note}
          </div>
        )}
      </div>
    </section>
  );
}

export function IronThumbsSection({ data }: { data: AllianceHubIronThumbs | null }) {
  if (!data || data.top.length === 0) return null;
  const [leader, ...rest] = data.top;
  return (
    <section className="space-y-2">
      <SectionTitle icon={<Flame size={14} />} label="Iron thumbs" />
      <div className="rounded-lg border border-emerald-800/50 bg-emerald-950/30 px-4 py-3">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-300">
          Most self-reports · past 7 days
        </div>
        <div className="mt-1 flex items-baseline justify-between gap-2">
          <span className="text-base font-semibold text-emerald-100">
            {playerLabel(leader.player)}
          </span>
          <span className="whitespace-nowrap text-xs tabular-nums text-emerald-200">
            {leader.count}× · +{leader.deltaM.toFixed(2)}M
          </span>
        </div>
        <div className="text-[10px] tabular-nums text-slate-400">
          avg +{leader.avgM.toFixed(2)}M per report
        </div>
        {leader.quip && (
          <div className="mt-1 text-[11px] italic text-emerald-200/90">
            …{leader.quip}
          </div>
        )}
        {rest.length > 0 && (
          <ul className="mt-2 space-y-0.5 text-[11px]">
            {rest.map((e, i) => (
              <li
                key={e.player.id}
                className="flex items-baseline justify-between gap-2 text-slate-300"
              >
                <span>
                  <span className="mr-1 text-slate-500 tabular-nums">
                    {i + 2}.
                  </span>
                  {playerLabel(e.player)}
                </span>
                <span className="tabular-nums text-slate-400">
                  {e.count}× · +{e.deltaM.toFixed(2)}M
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

export function GlowUpSection({ data }: { data: AllianceHubGlowUp | null }) {
  if (!data) return null;
  return (
    <section className="space-y-2">
      <SectionTitle icon={<Rocket size={14} />} label="Glow-up of the week" />
      <div className="rounded-lg border border-indigo-800/50 bg-indigo-950/30 px-4 py-3">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-indigo-300">
          Biggest M-Sum jump · last {data.days}d
        </div>
        <div className="mt-1 flex items-baseline justify-between gap-2">
          <span className="text-base font-semibold text-indigo-100">
            {playerLabel(data.player)}
          </span>
          <span className="whitespace-nowrap text-xs tabular-nums text-indigo-200">
            +{(data.pct * 100).toFixed(1)}%
          </span>
        </div>
        <div className="text-[10px] tabular-nums text-slate-400">
          {data.fromM.toFixed(1)} → {data.toM.toFixed(1)} Mio (+
          {(data.toM - data.fromM).toFixed(1)})
        </div>
        {data.quip && (
          <div className="mt-1 text-[11px] italic text-indigo-200/90">
            …{data.quip}
          </div>
        )}
      </div>
    </section>
  );
}

export function AboveWeightSection({ data }: { data: AllianceHubAboveWeight | null }) {
  if (!data) return null;
  return (
    <section className="space-y-2">
      <SectionTitle icon={<TrendingUp size={14} />} label="Punching above weight" />
      <div className="rounded-lg border border-teal-800/50 bg-teal-950/30 px-4 py-3">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-teal-300">
          Most VS per M of strength · last {data.weeks} weeks
        </div>
        <div className="mt-1 flex items-baseline justify-between gap-2">
          <span className="text-base font-semibold text-teal-100">
            {playerLabel(data.player)}
          </span>
          <span className="whitespace-nowrap text-xs tabular-nums text-teal-200">
            {(data.index / 100).toFixed(1)}× class median
          </span>
        </div>
        <div className="text-[10px] tabular-nums text-slate-400">
          {data.ratio.toFixed(2)} VS / M · median {data.vsMedian.toFixed(0)} M VS with{' '}
          {data.mSum.toFixed(1)} M-Sum
        </div>
        {data.quip && (
          <div className="mt-1 text-[11px] italic text-teal-200/90">…{data.quip}</div>
        )}
      </div>
    </section>
  );
}

export function BusterWeekSection({ data }: { data: AllianceHubBusterWeek | null }) {
  if (!data) return null;
  return (
    <section className="space-y-2">
      <SectionTitle icon={<Skull size={14} />} label="Most kills on buster day" />
      <div className="rounded-lg border border-orange-800/50 bg-orange-950/30 px-4 py-3">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-orange-300">
          Buster day {data.saturdayIso}
        </div>
        <div className="mt-1 flex items-baseline justify-between gap-2">
          <span className="text-base font-semibold text-orange-100">
            {playerLabel(data.player)}
          </span>
          <span className="whitespace-nowrap text-xs tabular-nums text-orange-200">
            +{formatKillsM(data.deltaKills)} M kills
          </span>
        </div>
        {data.quip && (
          <div className="mt-1 text-[11px] italic text-orange-200/90">
            …{data.quip}
          </div>
        )}
      </div>
    </section>
  );
}

export function SleepingBeautySection({
  data,
}: {
  data: AllianceHubSleepingBeauty | null;
}) {
  if (!data || data.top.length === 0) return null;
  const [leader, ...rest] = data.top;
  return (
    <section className="space-y-2">
      <SectionTitle icon={<Moon size={14} />} label="Sleeping beauties" />
      <div className="rounded-lg border border-yellow-800/40 bg-yellow-950/20 px-4 py-3">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-yellow-300">
          Longest without a self-report
        </div>
        <div className="mt-1 flex items-baseline justify-between gap-2">
          <span className="text-base font-semibold text-yellow-100">
            {playerLabel(leader.player)}
          </span>
          <span className="whitespace-nowrap text-xs tabular-nums text-yellow-200">
            {leader.days} days
          </span>
        </div>
        {leader.quip && (
          <div className="mt-1 text-[11px] italic text-yellow-200/90">
            …{leader.quip}
          </div>
        )}
        {rest.length > 0 && (
          <ul className="mt-2 space-y-0.5 text-[11px]">
            {rest.map((e, i) => (
              <li
                key={e.player.id}
                className="flex items-baseline justify-between gap-2 text-slate-300"
              >
                <span>
                  <span className="mr-1 text-slate-500 tabular-nums">
                    {i + 2}.
                  </span>
                  {playerLabel(e.player)}
                </span>
                <span className="tabular-nums text-slate-400">
                  {e.days}d
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function BestTrainStartTile({
  best,
}: {
  best: AllianceHubBestTrainStart | null;
}) {
  if (!best || best.considered === 0) return null;
  const nightLabel = nightWindowLabel();
  const nightName = QUALITY_LABEL.bad.toLowerCase();
  return (
    <p
      className="flex items-start gap-1.5 px-1 text-[11px] leading-snug text-slate-500"
      title={`"Prime time" = it's not ${nightName} (local ${nightLabel}) for the player.`}
    >
      <Clock size={12} className="mt-px shrink-0" />
      <span>
        Tip: best train start is{' '}
        <span className="tabular-nums text-slate-300">{best.serverStart}</span>{' '}
        server time — {best.count}/{best.considered} members in prime time,
        boarding open until{' '}
        <span className="tabular-nums">{best.serverEnd}</span> (
        {best.boardingHours}h).
      </span>
    </p>
  );
}

function TrainCountdownTile({ tile }: { tile: AllianceHubTrainTile }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const departsMs = Date.parse(tile.departsAt);
  const delta = departsMs - now;
  const isMega = tile.kind === 'mega_express';
  const border = isMega
    ? 'border-emerald-700/60 bg-emerald-950/30'
    : 'border-sky-700/60 bg-sky-950/30';
  const label = isMega ? 'Mega-Express is rolling' : 'Train is rolling';
  const accent = isMega ? 'text-emerald-100' : 'text-sky-100';
  const soft = isMega ? 'text-emerald-300' : 'text-sky-300';
  return (
    <section className={`rounded-lg border px-4 py-3 ${border}`}>
      <div className={`flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider ${soft}`}>
        {isMega ? <Sparkles size={12} /> : <Train size={12} />} {label}
      </div>
      <div className="mt-1 flex items-baseline gap-2">
        <span className={`text-2xl font-semibold tabular-nums ${accent}`}>
          {formatCountdown(delta)}
        </span>
        <span className="text-xs text-slate-400">until ETD</span>
      </div>
      <div className="mt-1 text-[11px] text-slate-300">
        Conductor:{' '}
        <span className={`font-semibold ${accent}`}>{playerLabel(tile.player)}</span>
        {tile.vip && (
          <>
            {' · '}
            <span className={soft}>VIP: {playerLabel(tile.vip.player)}</span>
          </>
        )}
      </div>
      {isMega && tile.score != null && (
        <div className={`mt-0.5 text-[11px] tabular-nums ${soft}`}>
          Score: {tile.score.toLocaleString()} pts
        </div>
      )}
      <div className="mt-1 text-[11px] italic text-slate-400">{tile.quip}</div>
    </section>
  );
}

export function LastMegaExpressSection({
  data,
}: {
  data: AllianceHubLastMegaExpress | null;
}) {
  if (!data) return null;
  return (
    <section className="space-y-2">
      <SectionTitle icon={<Gift size={14} />} label="Last Mega-Express" />
      <div className="rounded-lg border border-emerald-800/60 bg-emerald-950/20 px-4 py-3">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-300 tabular-nums">
          on {data.date}
        </div>
        <div className="mt-1 text-base font-semibold text-emerald-100">
          {playerLabel(data.player)}
        </div>
        {data.score != null && (
          <div className="text-[11px] text-slate-400 tabular-nums">
            {data.score.toLocaleString()} pts
          </div>
        )}
        {data.vip && (
          <div className="mt-0.5 text-[11px] text-emerald-300">
            VIP: {playerLabel(data.vip.player)}
          </div>
        )}
        <div className="mt-1 text-[11px] italic text-emerald-200/90">
          …{data.quip}
        </div>
      </div>
    </section>
  );
}

function ChatBansSection({
  entries,
}: {
  entries: AllianceHubChatBanEntry[];
}) {
  return (
    <section className="space-y-2">
      <SectionTitle
        icon={<MessageCircleOff size={14} />}
        label="Chat-ban wall"
      />
      {entries.length === 0 ? (
        <div className="rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-4 text-center text-sm text-slate-500">
          Everyone's talking. Chaos as usual.
        </div>
      ) : (
        <div className="space-y-2">
          {entries.map((e) => (
            <ChatBanRow key={e.ban.id} entry={e} />
          ))}
        </div>
      )}
    </section>
  );
}

function ChatBanRow({ entry }: { entry: AllianceHubChatBanEntry }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const endsMs = Date.parse(entry.ban.endsAtUtc);
  const delta = endsMs - now;
  return (
    <div className="rounded-lg border border-rose-900/40 bg-rose-950/20 px-3 py-2">
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-sm text-slate-100">
          <span className="font-medium">{playerLabel(entry.player)}</span>{' '}
          <span className="text-slate-400">has a</span>{' '}
          <span className="font-semibold text-rose-200">
            {entry.ban.durationHours}h chat ban
          </span>
        </div>
        <div className="whitespace-nowrap text-[11px] font-semibold tabular-nums text-rose-200">
          {formatCountdown(delta)}
        </div>
      </div>
      <div className="mt-0.5 text-[11px] italic text-slate-400">
        …{entry.quip}
      </div>
    </div>
  );
}

function ChatBanHighscoreSection({
  highscores,
}: {
  highscores: AllianceHubHighscore;
}) {
  if (
    highscores.count.length === 0 &&
    highscores.totalHours.length === 0
  ) {
    return null;
  }
  return (
    <section className="space-y-2">
      <SectionTitle icon={<Trophy size={14} />} label="Chat-ban hall of fame" />
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <HighscoreCard title="Most bans">
          {highscores.count.length === 0 ? (
            <EmptyRow />
          ) : (
            highscores.count.map((row, i) => (
              <HighscoreRow
                key={row.player.id}
                rank={i + 1}
                label={playerLabel(row.player)}
                value={`${row.count}×`}
              />
            ))
          )}
        </HighscoreCard>
        <HighscoreCard title="Longest total silence">
          {highscores.totalHours.length === 0 ? (
            <EmptyRow />
          ) : (
            highscores.totalHours.map((row, i) => (
              <HighscoreRow
                key={row.player.id}
                rank={i + 1}
                label={playerLabel(row.player)}
                value={formatHours(row.totalHours)}
              />
            ))
          )}
        </HighscoreCard>
      </div>
    </section>
  );
}

function formatHours(h: number): string {
  if (h < 48) return `${h}h`;
  const days = Math.floor(h / 24);
  const rest = h % 24;
  return rest === 0 ? `${days}d` : `${days}d ${rest}h`;
}

function HighscoreCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-2">
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
        {title}
      </div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function HighscoreRow({
  rank,
  label,
  value,
}: {
  rank: number;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-baseline gap-2 text-xs">
      <span className="w-4 text-right font-semibold tabular-nums text-slate-500">
        {rank}
      </span>
      <span className="flex-1 truncate text-slate-200">{label}</span>
      <span className="tabular-nums text-slate-300">{value}</span>
    </div>
  );
}

function EmptyRow() {
  return <div className="py-1 text-[11px] text-slate-500">—</div>;
}

export function VsSlackersSection({
  slackers,
}: {
  slackers: AllianceHubVsSlackers | null;
}) {
  if (!slackers || slackers.players.length === 0) return null;
  return (
    <section className="space-y-2">
      <SectionTitle
        icon={<AlertTriangle size={14} />}
        label="Under 7.2 M yesterday"
      />
      <div className="rounded-lg border border-amber-900/40 bg-amber-950/20 px-3 py-2">
        <div className="space-y-2">
          {slackers.players.map((s) => (
            <div key={s.player.id} className="text-sm">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-slate-100">
                  <span className="font-medium">
                    {playerLabel(s.player)}
                  </span>
                </span>
                <span className="whitespace-nowrap text-xs tabular-nums text-amber-200">
                  {(s.scoreTenthsM / 10).toFixed(1)} M
                </span>
              </div>
              <div className="text-[11px] italic text-slate-400">
                …{s.quip}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function StrengthTopSection({
  top,
}: {
  top: AllianceHubStrengthTop | null;
}) {
  if (!top) return null;
  return (
    <section className="space-y-2">
      <SectionTitle
        icon={<TrendingUp size={14} />}
        label={`Strength push · ${top.periodLabel}`}
      />
      <div className="text-[11px] text-slate-500">
        {top.ranked} {top.ranked === 1 ? 'player' : 'players'} ranked — growth
        over the whole month, same window for everyone.
        {top.notComparable > 0 &&
          ` ${top.notComparable} more reported but had no report within ${top.maxGapDays} days of the month's start or end.`}
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <StrengthTile
          title="Biggest M1 jump"
          entry={top.m1}
          emphasis="m1"
        />
        <StrengthTile
          title="Biggest total jump"
          entry={top.mSum}
          emphasis="sum"
        />
        <StrengthTile
          title="Biggest % jump"
          entry={top.pct}
          emphasis="pct"
        />
      </div>
    </section>
  );
}


function StrengthTile({
  title,
  entry,
  emphasis,
}: {
  title: string;
  entry: (AllianceHubStrengthEntry & { pct?: number }) | null;
  emphasis: 'm1' | 'sum' | 'pct';
}) {
  return (
    <div className="rounded-lg border border-emerald-900/50 bg-emerald-950/20 px-3 py-3">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-300">
        {title}
      </div>
      {entry ? (
        <>
          <div className="mt-1 flex items-baseline gap-1">
            <Sparkles size={14} className="text-emerald-300" />
            <span className="text-xl font-semibold tabular-nums text-emerald-100">
              {emphasis === 'pct' && entry.pct != null
                ? `+${entry.pct.toFixed(1)} %`
                : `+${entry.deltaM.toFixed(2)} M`}
            </span>
          </div>
          <div className="text-sm text-slate-200">
            {playerLabel(entry.player)}
          </div>
          <div
            className="text-[11px] text-slate-500 tabular-nums"
            title={estimateHint(entry.fromEstimatedFrom, entry.toEstimatedFrom)}
          >
            {entry.fromEstimatedFrom ? '≈' : ''}
            {entry.fromM.toFixed(2)} → {entry.toEstimatedFrom ? '≈' : ''}
            {entry.toM.toFixed(2)}{' '}
            {emphasis === 'm1' ? '(M1)' : '(ΣM)'}
            {emphasis === 'pct' && ` · +${entry.deltaM.toFixed(2)} M`}
          </div>
          <div className="mt-1 text-[11px] italic text-slate-400">
            …{entry.quip}
          </div>
        </>
      ) : (
        <div className="mt-1 text-xs text-slate-500">
          No standout this round.
        </div>
      )}
    </div>
  );
}

export function SectionTitle({
  icon,
  label,
}: {
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-slate-400">
      <span className="text-slate-500">{icon}</span>
      {label}
    </div>
  );
}
